/**
 * Sponsored Ads API — Supabase PostgREST queries.
 */

import supabase from "../config/supabase";

const AD_SELECT = `
  *,
  categories:sponsored_ad_categories(category_id, categories(*)),
  locations:sponsored_ad_locations(location_id, locations(*)),
  user:users(id, first_name, last_name, profile_image_url, role, is_placeholder),
  business:businesses(id, name, abn, is_verified)
`;

const invokeAiModeration = async (body) => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) return;
  supabase.functions.invoke("ai-moderation", {
    body,
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
};

const sponsoredAdsApi = {
  async list(params = {}) {
    let query = supabase.from("sponsored_ads").select(AD_SELECT);

    // Owners see their own ads at any status; everyone else sees only active.
    if (params.user_id) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id === params.user_id) {
        query = query.eq("user_id", params.user_id);
      } else {
        query = query.eq("status", "active").eq("user_id", params.user_id);
      }
    } else {
      query = query.eq("status", "active");
    }

    query = query.order("created_at", { ascending: false });
    if (params.limit) query = query.limit(params.limit);

    const { data, error } = await query;
    if (error) return { success: false, error: error.message };

    let items = (data || []).map(flattenAd);

    if (params.category) {
      const slugs = params.category.split("|");
      items = items.filter((ad) =>
        ad.categories?.some((cat) => slugs.includes(cat.slug))
      );
    }

    if (params.location) {
      const names = params.location.split("|");
      items = items.filter((ad) =>
        ad.locations?.some((loc) => names.includes(loc.name))
      );
    }

    return { success: true, data: { items, total: items.length } };
  },

  async get(adId) {
    const { data, error } = await supabase
      .from("sponsored_ads")
      .select(AD_SELECT)
      .eq("id", adId)
      .single();
    if (error) return { success: false, error: error.message };
    return { success: true, data: flattenAd(data) };
  },

  async create(body) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const { categories: catSlugs, locations: locNames, ...adData } = body;
    adData.user_id = user.id;
    adData.status = "pending";

    const { data, error } = await supabase.from("sponsored_ads").insert(adData).select().single();
    if (error) return { success: false, error: error.message };

    if (catSlugs?.length) {
      const { data: cats } = await supabase.from("categories").select("id").in("slug", catSlugs);
      if (cats?.length) {
        await supabase.from("sponsored_ad_categories").insert(
          cats.map((c) => ({ ad_id: data.id, category_id: c.id }))
        );
      }
    }

    if (locNames?.length) {
      const { data: locs } = await supabase.from("locations").select("id").in("name", locNames);
      if (locs?.length) {
        await supabase.from("sponsored_ad_locations").insert(
          locs.map((l) => ({ ad_id: data.id, location_id: l.id }))
        );
      }
    }

    if (data.status === "pending") {
      invokeAiModeration({ item_id: data.id, item_type: "sponsored_ad" });
    }

    return { success: true, data };
  },

  async update(adId, body) {
    const { data, error } = await supabase.from("sponsored_ads").update(body).eq("id", adId).select().single();
    if (error) return { success: false, error: error.message };
    return { success: true, data };
  },

  async submit(adId) {
    const { data: ad, error } = await supabase
      .from("sponsored_ads")
      .select("status, title, description")
      .eq("id", adId)
      .single();
    if (error) return { success: false, error: error.message };
    if (ad?.status !== "pending") return { success: true };

    if (ad) {
      invokeAiModeration({ item_id: adId, item_type: "sponsored_ad" });
    }

    return { success: true };
  },

  async delete(adId) {
    const { error } = await supabase.from("sponsored_ads").delete().eq("id", adId);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },
};

function flattenAd(row) {
  if (!row) return row;
  return {
    ...row,
    categories: (row.categories || []).map((jt) => jt.categories).filter(Boolean),
    locations: (row.locations || []).map((jt) => jt.locations).filter(Boolean),
  };
}

export default sponsoredAdsApi;
